import { useMemo } from "react";

import { typedQueryClient } from "hooks";
import { CarDetailsQueryKeys } from "modules/CarDetails/queryKeys";

import type { WebSocketMessageListener } from "components";
import type { Customer, CustomerNote } from "models";

const upsertCustomerNote = (customer: Customer, note: CustomerNote) => {
  const noteIdx = customer.notes?.findIndex(n => n.id === note.id) ?? -1;

  const updatedCustomerNotes = [...(customer.notes ?? [])];
  if (noteIdx < 0) updatedCustomerNotes.push(note);
  else updatedCustomerNotes[noteIdx] = note;

  return updatedCustomerNotes;
};

const removeCustomerNote = (customer: Customer, note: CustomerNote) => {
  if (!customer.notes) return;

  const noteIdx = customer.notes.findIndex(n => n.id === note.id);
  if (noteIdx < 0) return customer.notes;

  return customer.notes.toSpliced(noteIdx, 1);
};

export const useCustomerInfoListeners = (appointment_id: number) => {
  const listeners = useMemo(() => {
    const customerNoteListeners: WebSocketMessageListener<CustomerNote>[] = [
      {
        model: "CustomerNote",
        action: "upsert",
        callback: ({ data: note }) => {
          const appointment = typedQueryClient.getQueryData(CarDetailsQueryKeys.customerInfo(appointment_id));
          if (!appointment) return;

          const { customer_owner, customer_driver, customer_contract } = appointment;
          if (![customer_owner?.id, customer_driver?.id, customer_contract?.id].includes(note.customer_id)) return;

          typedQueryClient.setQueryData(CarDetailsQueryKeys.customerInfo(appointment_id), {
            ...appointment,
            ...(customer_owner?.id === note.customer_id && { customer_owner: { ...customer_owner, notes: upsertCustomerNote(customer_owner, note) } }),
            ...(customer_driver?.id === note.customer_id && { customer_driver: { ...customer_driver, notes: upsertCustomerNote(customer_driver, note) } }),
            ...(customer_contract?.id === note.customer_id && { customer_contract: { ...customer_contract, notes: upsertCustomerNote(customer_contract, note) } })
          });
        }
      },
      {
        model: "CustomerNote",
        action: "delete",
        callback: ({ data: note }) => {
          const appointment = typedQueryClient.getQueryData(CarDetailsQueryKeys.customerInfo(appointment_id));
          if (!appointment) return;

          const { customer_owner, customer_driver, customer_contract } = appointment;
          const isDriverNote = customer_driver?.id === note.customer_id;
          const isOwnerNote = customer_owner?.id === note.customer_id;
          const isContractorNote = customer_contract?.id === note.customer_id;

          if (
            (!isDriverNote && !isOwnerNote && !isContractorNote) ||
            (isOwnerNote && !customer_owner.notes?.length) ||
            (isDriverNote && !customer_driver.notes?.length) ||
            (isContractorNote && !customer_contract.notes?.length)
          )
            return;

          typedQueryClient.setQueryData(CarDetailsQueryKeys.customerInfo(appointment_id), {
            ...appointment,
            ...(isOwnerNote && { customer_owner: { ...customer_owner, notes: removeCustomerNote(customer_owner, note) } }),
            ...(isDriverNote && { customer_driver: { ...customer_driver, notes: removeCustomerNote(customer_driver, note) } }),
            ...(isContractorNote && { customer_contract: { ...customer_contract, notes: removeCustomerNote(customer_contract, note) } })
          });
        }
      }
    ];

    return [...customerNoteListeners];
  }, [appointment_id]);

  return listeners;
};
